import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { MediaIdField } from '@/features/media';

import {
  MAX_FAQ_ITEMS,
  MAX_STEPS,
  templateBlocks,
  type PageFormInput,
} from '../schemas/page-form-schema';

function ItemControls({
  index,
  count,
  label,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  label: string;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useTranslation('pages');
  return (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        disabled={index === 0}
        aria-label={t('blocks.moveUp', { item: label })}
        onClick={() => onMove(index, index - 1)}
      >
        <ArrowUp aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        disabled={index === count - 1}
        aria-label={t('blocks.moveDown', { item: label })}
        onClick={() => onMove(index, index + 1)}
      >
        <ArrowDown aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="text-destructive hover:text-destructive size-8"
        aria-label={t('blocks.remove', { item: label })}
        onClick={() => onRemove(index)}
      >
        <Trash2 aria-hidden />
      </Button>
    </div>
  );
}

function FaqItems() {
  const { t } = useTranslation('pages');
  const form = useFormContext<PageFormInput>();
  const items = useFieldArray({ control: form.control, name: 'blocks.faq' });

  return (
    <FormSection title={t('blocks.faqTitle')} description={t('blocks.faqDescription')}>
      {items.fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('blocks.faqEmpty')}</p>
      ) : (
        <ol className="flex flex-col gap-4">
          {items.fields.map((item, index) => {
            const label = t('blocks.faqItem', { n: index + 1 });
            return (
              <li
                key={item.id}
                aria-label={label}
                className="flex flex-col gap-3 rounded-md border p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{label}</span>
                  <ItemControls
                    index={index}
                    count={items.fields.length}
                    label={label}
                    onMove={items.move}
                    onRemove={items.remove}
                  />
                </div>
                <FormField
                  control={form.control}
                  name={`blocks.faq.${index}.question`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('blocks.question')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`blocks.faq.${index}.answer`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('blocks.answer')}</FormLabel>
                      <FormControl>
                        <Textarea rows={3} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </li>
            );
          })}
        </ol>
      )}
      <FormField
        control={form.control}
        name="blocks.faq"
        render={() => (
          <FormItem>
            <FormMessage />
          </FormItem>
        )}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={items.fields.length >= MAX_FAQ_ITEMS}
        onClick={() => items.append({ question: '', answer: '' })}
      >
        <Plus aria-hidden />
        {t('blocks.addQuestion')}
      </Button>
    </FormSection>
  );
}

function Steps() {
  const { t } = useTranslation('pages');
  const form = useFormContext<PageFormInput>();
  const steps = useFieldArray({ control: form.control, name: 'blocks.steps' });

  return (
    <FormSection title={t('blocks.stepsTitle')} description={t('blocks.stepsDescription')}>
      {steps.fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('blocks.stepsEmpty')}</p>
      ) : (
        <ol className="flex flex-col gap-4">
          {steps.fields.map((step, index) => {
            const label = t('blocks.step', { n: index + 1 });
            return (
              <li
                key={step.id}
                aria-label={label}
                className="flex flex-col gap-3 rounded-md border p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{label}</span>
                  <ItemControls
                    index={index}
                    count={steps.fields.length}
                    label={label}
                    onMove={steps.move}
                    onRemove={steps.remove}
                  />
                </div>
                <FormField
                  control={form.control}
                  name={`blocks.steps.${index}.name`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('blocks.stepName')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`blocks.steps.${index}.text`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('blocks.stepText')}</FormLabel>
                      <FormControl>
                        <Textarea rows={3} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </li>
            );
          })}
        </ol>
      )}
      <FormField
        control={form.control}
        name="blocks.steps"
        render={() => (
          <FormItem>
            <FormMessage />
          </FormItem>
        )}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={steps.fields.length >= MAX_STEPS}
        onClick={() => steps.append({ name: '', text: '' })}
      >
        <Plus aria-hidden />
        {t('blocks.addStep')}
      </Button>
    </FormSection>
  );
}

function Hero() {
  const { t } = useTranslation('pages');
  const form = useFormContext<PageFormInput>();
  const enabled = form.watch('blocks.hero.enabled');

  return (
    <FormSection title={t('blocks.heroTitle')} description={t('blocks.heroDescription')}>
      <FormField
        control={form.control}
        name="blocks.hero.enabled"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center gap-3">
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <FormLabel>{t('blocks.heroEnabled')}</FormLabel>
          </FormItem>
        )}
      />
      {enabled ? (
        <>
          <FormField
            control={form.control}
            name="blocks.hero.heading"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('blocks.heading')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="blocks.hero.subheading"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('blocks.subheading')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="blocks.hero.mediaId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('blocks.heroImage')}</FormLabel>
                <MediaIdField
                  value={field.value}
                  onChange={field.onChange}
                  label={t('blocks.heroImageLabel')}
                />
                <FormDescription>{t('blocks.heroImageHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      ) : null}
    </FormSection>
  );
}

/** The structured blocks of a page: hero on every template, FAQ and steps where the template renders them. */
export function PageBlocksSection() {
  const form = useFormContext<PageFormInput>();
  const uses = templateBlocks(form.watch('template'));
  return (
    <>
      <Hero />
      {uses.faq ? <FaqItems /> : null}
      {uses.steps ? <Steps /> : null}
    </>
  );
}
